/**
 * Message 的扩展组件
 * 基于会话、消息业务数据拼接基本的消息渲染框架
 */
import { Message, MessageProps } from 'components/Dialogue';
import { getMetaFromSender, timeFormatter } from '../../utils';
import { MessageExtendFooter } from './Footer';
import { DOMAttributes, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { UIMessages } from '../../useChatView';
import { useTranslate } from '@tolgee/react';
import { LocalMessage, useConversationReadonlyValue } from 'hooks/business/conversation';
import { MessageExtendAvatar } from './Avartar';
import { useMemoizedFn } from 'ahooks';
import { getVideoUrl, isSupportMenu } from '../MessageMenu/ContextMenu';
import { css } from '@emotion/css';
import ExtraMenu, { Placement } from '../MessageMenu/ExtraMenu';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useChatContext } from '../../contexts/ChatContext';
import { QIFeature } from 'utils/featureFlag';
import { DeductionMenu } from 'packages/qualityInspection/pages/QiTask/Deduction';
import { buyerNickMask } from 'services/channels/demo';
import VideoText from '../VideoText';
import { DialogueModeContext } from '../DialogueModeContext.tsx';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import { useIframeModeContext } from 'views/Galaxy/IframeModeContext.tsx';
import { FailCauseContent } from '../MessageStatusDecorate.tsx';

export interface MessageExtendProps extends Omit<MessageProps, 'name' | 'time'> {
  raw: LocalMessage;
  msg: UIMessages;
  conversation: Conversation.Conversation;
  card?: ReactNode;
  showSourceContent?: boolean;
  onContextMenu?: DOMAttributes<HTMLDivElement>['onContextMenu'];
}

export const MessageExtend = (props: MessageExtendProps) => {
  const { msg, conversation, content, card } = props;
  const { sender, nickName, time, raw, status } = msg;
  const { channel, topBarInfo } = conversation;
  const { t } = useTranslate();
  const sysLang = useSystemLanguageValue();
  const readonly = useConversationReadonlyValue();

  const { iframeMode } = useIframeModeContext();

  const { hideFooter } = useChatContext();
  const isFaq = useContext(DialogueModeContext) === 'faq';

  const [videoText, setVideoText] = useState<string>();

  const {
    singleTransToSysLang,
    showFeedback,
    isGalaxy,
    showContextMenu,
    setContextMenuMessage,
    setVideoTextState,
  } = useChatContext();

  const { isBuyerMessage, isRobotMessage, layout } = useMemo(() => {
    return getMetaFromSender(sender);
  }, [sender]);

  const showName = useMemo(() => {
    if (isRobotMessage) {
      return (
        <b>
          [{topBarInfo?.storeName}-{t('domain.aiReception.agent.lechat')}]
        </b>
      );
    }
    return buyerNickMask(nickName);
  }, [isRobotMessage, topBarInfo?.storeName, nickName, t]);

  const extraMenuRender = useMemoizedFn((placement: Placement) =>
    QIFeature ? (
      <DeductionMenu
        uiMessage={msg}
        conversation={conversation}
        placement={placement}
        onTranslate={singleTransToSysLang}
        targetLanguage={sysLang}
      />
    ) : (
      <ExtraMenu
        uiMessage={msg}
        conversation={conversation}
        isGalaxy={isGalaxy}
        onTranslate={singleTransToSysLang}
        onFeedback={showFeedback}
        channel={channel}
        targetLanguage={sysLang}
        placement={placement}
        videoText={videoText}
        setVideoText={setVideoText}
      />
    ),
  );

  const handleContextMenu = useMemoizedFn((e) => {
    e.preventDefault();

    setContextMenuMessage(msg);

    setVideoTextState([videoText, setVideoText]);

    setTimeout(() => {
      showContextMenu(e);
    });
  });

  const contentWithContextMenuRenderer = useMemoizedFn((content: ReactNode) => {
    const isLightWeightErrorTextFallback =
      msg?.raw?.textContent?.failCause?.code === 'LIGHTWEIGHT_BOT_SENT_LIMIT_REACHED';
    if (isLightWeightErrorTextFallback) return null;

    if (msg.type === 'imageWithText' || msg.type === 'facebook') {
      return [card, content];
    }

    const { isSupportExtra } = isSupportMenu(msg, conversation, isGalaxy, readonly);

    if (!isSupportExtra) {
      return content;
    }

    return (
      <>
        {card}
        <div className={menuContainer}>
          {layout === 'right' && extraMenuRender('left')}
          <div
            className={`flex flex-col ${layout === 'right' ? 'items-end' : 'items-start'}`}
            onContextMenu={handleContextMenu}
          >
            {content}
          </div>
          {layout === 'left' && extraMenuRender('right')}
        </div>
      </>
    );
  });

  const getVideoAsrTextBody = useCallback(() => {
    return getVideoUrl({
      channel,
      msgId: raw.msgId,
      conversationId: raw.conversationId,
      videoId: raw.videoContent?.videoId,
      quality: raw.videoContent?.quality,
      videoUrl: (msg?.content as any)?.url,
    }).then((url) => {
      return {
        marketplace: raw.channel,
        region: raw.region,
        storeId: raw.storeId,
        source: 'message',
        sourceId: raw.msgId,
        videoPath: url,
      } as const;
    });
  }, [channel, raw, msg?.content]);

  if (isFaq) {
    return (
      <FaqContainer layout={layout}>
        {content}
        <span>
          {layout === 'right' && (
            <div>{t('scene.chatWorkspace.messageThread.aiAssistant.sentByStoreAi')}</div>
          )}
          <div>{dayjs(time).format('HH:mm')}</div>
        </span>
      </FaqContainer>
    );
  }

  return (
    <Message
      avatar={
        <MessageExtendAvatar
          {...props}
          isBuyerMessage={isBuyerMessage}
          isRobotMessage={isRobotMessage}
        />
      }
      time={time}
      timeFormatter={timeFormatter}
      name={showName}
      layout={layout}
      content={contentWithContextMenuRenderer(content)}
      footer={[
        status === 'error' && <FailCauseContent message={raw} failCause={raw.failCause} />,
        ...(hideFooter
          ? []
          : [
              raw.messageType === 'VIDEO' &&
                msg.raw.msgFromType === 'BUYER' &&
                videoText !== undefined && (
                  <VideoText
                    key="video-text"
                    region={raw.region}
                    videoText={videoText}
                    setVideoText={setVideoText}
                    getVideoAsrTextBody={getVideoAsrTextBody}
                  />
                ),
              <MessageExtendFooter key="footer" {...props} layout={layout} />,
            ]),
      ]}
      msgId={raw.msgId}
      iframeMode={iframeMode}
    />
  );
};

const menuContainer = css`
  display: flex;
  align-items: flex-end;

  > div {
    flex: 1 0 0;
  }

  .menu-content {
    display: flex;
    flex-direction: column;
  }
  .menu-content-left {
    align-items: start;
  }
  .menu-content-right {
    align-items: end;
  }
  .hoverVisible {
    visibility: hidden;
  }
  &:hover {
    .hoverVisible {
      visibility: visible;
    }
  }
`;

const FaqContainer = styled.div<{ layout: 'left' | 'right' }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ layout }) => (layout === 'left' ? 'flex-start' : 'flex-end')};
  > span {
    display: flex;
    align-items: center;
    margin-top: 8px;
    > div {
      font-size: 12px;
      color: #84868c;
      & + div {
        margin-left: 14px;
      }
    }
  }
`;
